import React, { Suspense, useEffect, useRef, useState } from 'react'
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useMediaQuery } from 'react-responsive';
import VideoPlayer from './VideoPlayer';
import useDynamicMediaQuery from '../hooks/useDynamicMediaQuery';

gsap.registerPlugin(ScrollTrigger);

const VideoOnScroll = ({videoWebm, videoMp4, posterUrl}) => {
	const prefersReducedMotion = useMediaQuery({
		query: '(prefers-reduced-motion: reduce)',
	});
	const [isPlaying, setIsPlaying] = useState(!prefersReducedMotion);
	const isDesktop = useDynamicMediaQuery(768);
	const parentRef = useRef(null);
	const videoContainerRef = useRef(null);
	const toggleRef = useRef(null);
	const togglePlay = () => setIsPlaying((playing) => !playing);

	// Pause whenever reduced motion is enabled. The control remains available
	// so the visitor can still choose to play the video.
	useEffect(() => {
		if (prefersReducedMotion) setIsPlaying(false);
	}, [prefersReducedMotion]);

	useGSAP(() => {
		const parent = parentRef.current;
		const container = videoContainerRef.current;
		const toggle = toggleRef.current;
		const isBlockEditor = document.body.classList.contains("block-editor-page");

		if (!parent || !container || !toggle || isBlockEditor || prefersReducedMotion) return;

		// Clear any desktop scale left behind when resizing down across the
		// breakpoint. Mobile always displays the normal full-width 16:9 media.
		if (!isDesktop) {
			gsap.set([container, toggle], { clearProps: "transform" });
			return;
		}

		const mediaQuery = gsap.matchMedia();

		mediaQuery.add("(prefers-reduced-motion: no-preference)", () => {
			const timeline = gsap.timeline({
				scrollTrigger: {
					trigger: parent,
					pin: true,
					start: "top top",
					end: () => `+=${window.innerHeight}`,
					// Tie animation progress to scroll position with a small amount of
					// smoothing instead of playing a time-based animation.
					scrub: 1,
					anticipatePin: 1,
					// Recalculate the endpoint after viewport/orientation changes.
					invalidateOnRefresh: true,
				},
			});

			timeline
				.fromTo(
					container,
					{ scale: 0.32, y: -128 },
					{ scale: 1, y: 0, ease: "none" },
					0,
				)
				.fromTo(toggle, { y: -128 }, { y: 0, ease: "none" }, 0);
		});

		// Remove the media-query context and its ScrollTrigger when React unmounts
		// or rerenders this block in a different context.
		return () => mediaQuery.revert();
	}, {
		scope: parentRef,
		dependencies: [isDesktop, prefersReducedMotion],
		revertOnUpdate: true,
	});

	let playerProps = {
		muted: true,
		playing: isPlaying,
		playsInline: true,
		loop: true,
		controls: false,
		poster: posterUrl,
		style: { objectFit: "cover" },
	};

	return (
		<div ref={parentRef} className="video-scroll">
			<div ref={videoContainerRef} className="video-scroll__container">
				<Suspense fallback={<img src={posterUrl} alt="" />}>
					<VideoPlayer
						videoUrls={[videoWebm, videoMp4]}
						posterUrl={posterUrl}
						config={playerProps}
					/>
				</Suspense>
			</div>
			{/* Keep the control outside the scaled container so it stays full-size. */}
			<button
				ref={toggleRef}
				className={`video-toggle button-reset ${isPlaying ? "is-playing" : "is-paused"}`}
				onClick={togglePlay}
				aria-label={isPlaying ? "Pause video" : "Play video"}
			/>
		</div>
	);
};

export default VideoOnScroll;
